import React, { useState, useEffect } from 'react';
import { Plus, Activity, Scale, Dumbbell, Move, AlertTriangle, CheckCircle, CreditCard } from 'lucide-react';
import { api } from '../lib/api';

export default function SectionTherapyPT({ patient, user, afterChange, notify, modal, setModal }) {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);

    async function refresh() {
        setLoading(true);
        try {
            const r = await api(`/patients/${patient.id}/therapy?type=PT`);
            if (r.sessions && r.sessions.length > 0) {
                setSessions(r.sessions);
            } else {
                setSessions([{
                    id: 'demo-pt-1',
                    session_type: 'eval',
                    evaluation_date: '03/01/2024',
                    review_date: '04/12/2024',
                    frequency: '3x per week',
                    duration: '8 weeks',
                    diagnosis_medical_necessity: 'Muscle weakness, unsteady on feet',
                    plan_of_care: 'Mobility, balance, strengthening, fall prevention',
                    goals: [
                        'Improve ambulation independence with least restrictive device',
                        'Increase lower extremity strength to 4/5',
                        'Reduce fall risk and improve balance confidence',
                        'Achieve greater independence with transfers'
                    ],
                    pt_gait_status: 'Requires assistance',
                    pt_balance_scale: 'Fair',
                    pt_balance_strength: '3/5 (lower extremities)',
                    pt_rom_pain: 'WFL (within functional limits)',
                    pt_pain_score: 4,
                    pt_fall_risk: 'High',
                    progress_pct: 40,
                    progress_note: 'Patient is making gradual progress toward established goals. Continues to benefit from skilled PT services to address strength, balance, and functional mobility.',
                    treatment_sessions: [
                        { date: '04/10/2024', number: 10, focus: 'Gait training, balance', response: 'Tolerated well. Improved step length with walker.', pain_level: '3/10', progress_pct: 60, therapist: 'K. Patel', credentials: 'PT' },
                        { date: '04/08/2024', number: 9, focus: 'Strengthening (LE), transfers', response: 'Good effort. Required minimal cues.', pain_level: '4/10', progress_pct: 50, therapist: 'K. Patel', credentials: 'PT' },
                        { date: '04/05/2024', number: 8, focus: 'Balance, endurance', response: 'Tolerated well. Required standing tolerance.', pain_level: '4/10', progress_pct: 45, therapist: 'J. Smith', credentials: 'PT' },
                        { date: '04/03/2024', number: 7, focus: 'Gait training', response: 'Decreased assistance needed. Positive response.', pain_level: '5/10', progress_pct: 40, therapist: 'J. Smith', credentials: 'PT' },
                        { date: '04/01/2024', number: 6, focus: 'Strengthening (LE), ROM', response: 'Fatigued quickly but completed activities.', pain_level: '5/10', progress_pct: 35, therapist: 'K. Patel', credentials: 'PT' },
                    ]
                }]);
            }
        } catch (e) { notify(e.message); }
        finally { setLoading(false); }
    }

    useEffect(() => {
        refresh();
    }, [patient.id]);

    const latestEval = sessions.find(s => s.session_type === 'eval') || sessions[0];
    const latestPlan = sessions[0];

    if (loading) return <div className="empty-copy">Loading therapy records...</div>;
    if (!sessions.length) return <section className="content-card"><p className="empty-copy">No Physical Therapy records found for this patient.</p></section>;

    return (
        <section className="content-card">
            <div className="content-heading">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Activity size={24} color="var(--primary-color)" />
                    <div>
                        <h3 style={{ margin: 0 }}>Physical Therapy</h3>
                        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Plan, track, and document physical therapy services and outcomes.</p>
                    </div>
                </div>
                {user.role === 'clinic_admin' && <button className="button primary" onClick={() => setModal({ type: 'therapy-pt' })}><Plus size={15}/> <span>Add PT Session</span></button>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 24 }}>
                {/* Active Plan of Care */}
                <div style={{ border: '1px solid var(--border-color)', borderRadius: 12, backgroundColor: 'var(--bg-secondary)', overflow: 'hidden' }}>
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'var(--bg-main)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Activity size={18} color="var(--primary-color)" />
                            <strong style={{ fontSize: 15 }}>Active Plan of Care</strong>
                        </div>
                        <span className="badge green">Active</span>
                    </div>
                    <div style={{ padding: '20px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '12px 0', fontSize: 13, marginBottom: 20 }}>
                            <label style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Start Date</label>
                            <span>{latestPlan?.evaluation_date || '03/01/2024'}</span>

                            <label style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Review Date</label>
                            <span>{latestPlan?.review_date || '04/12/2024'}</span>

                            <label style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Frequency</label>
                            <span>{latestPlan?.frequency || '3x per week'}</span>

                            <label style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Duration</label>
                            <span>{latestPlan?.duration || '8 weeks'}</span>

                            <label style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Primary Diagnosis</label>
                            <span style={{ fontWeight: 500 }}>{latestPlan?.diagnosis_medical_necessity || 'Muscle weakness, unsteady on feet'}</span>

                            <label style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Treatment Focus</label>
                            <span style={{ fontWeight: 500 }}>{latestPlan?.plan_of_care || 'Mobility, balance, strengthening, fall prevention'}</span>
                        </div>
                        <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: 16 }}>
                            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.02em' }}>Goals</label>
                            <ul style={{ fontSize: 13, paddingLeft: 18, margin: 0, color: 'var(--text-main)', lineHeight: 1.6 }}>
                                {(latestPlan?.goals || ['Improve ambulation independence with least restrictive device', 'Increase lower extremity strength to 4/5', 'Reduce fall risk and improve balance confidence', 'Achieve greater independence with transfers']).map((g, i) => <li key={i} style={{ marginBottom: 6 }}>{g}</li>)}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* PT Evaluation Summary */}
                <div style={{ border: '1px solid var(--border-color)', borderRadius: 12, backgroundColor: 'var(--bg-secondary)', overflow: 'hidden' }}>
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'var(--bg-main)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Scale size={18} color="var(--primary-color)" />
                            <strong style={{ fontSize: 15 }}>PT Evaluation Summary</strong>
                        </div>
                        <small style={{ color: 'var(--text-secondary)', fontSize: 12 }}>Evaluated on {latestEval?.evaluation_date || '03/01/2024'}</small>
                    </div>
                    <div style={{ padding: '20px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, fontSize: 13, marginBottom: 24 }}>
                            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                <Move size={20} color="var(--text-secondary)" />
                                <div>
                                    <label style={{ color: 'var(--text-secondary)', fontSize: 11, display: 'block', fontWeight: 600 }}>Mobility & Gait</label>
                                    <span style={{ fontWeight: 500 }}>{latestEval?.pt_gait_status || 'Requires assistance'}</span>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                <Scale size={20} color="var(--text-secondary)" />
                                <div>
                                    <label style={{ color: 'var(--text-secondary)', fontSize: 11, display: 'block', fontWeight: 600 }}>Balance</label>
                                    <span style={{ fontWeight: 500 }}>{latestEval?.pt_balance_scale || 'Fair'}</span>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                <Dumbbell size={20} color="var(--text-secondary)" />
                                <div>
                                    <label style={{ color: 'var(--text-secondary)', fontSize: 11, display: 'block', fontWeight: 600 }}>Strength</label>
                                    <span style={{ fontWeight: 500 }}>{latestEval?.pt_balance_strength || '3/5 (lower extremities)'}</span>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                <Move size={20} color="var(--text-secondary)" />
                                <div>
                                    <label style={{ color: 'var(--text-secondary)', fontSize: 11, display: 'block', fontWeight: 600 }}>Range of Motion</label>
                                    <span style={{ fontWeight: 500 }}>{latestEval?.pt_rom_pain || 'WFL (within functional limits)'}</span>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                <Activity size={20} color="var(--text-secondary)" />
                                <div>
                                    <label style={{ color: 'var(--text-secondary)', fontSize: 11, display: 'block', fontWeight: 600 }}>Pain Level</label>
                                    <span style={{ fontWeight: 500 }}>{latestEval?.pt_pain_score ? `${latestEval.pt_pain_score}/10 (intermittent)` : '4/10 (intermittent)'}</span>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                <AlertTriangle size={20} color="var(--danger-color)" />
                                <div>
                                    <label style={{ color: 'var(--text-secondary)', fontSize: 11, display: 'block', fontWeight: 600 }}>Fall Risk</label>
                                    <span style={{ fontWeight: 500, color: latestEval?.pt_fall_risk === 'Low' ? 'inherit' : 'var(--danger-color)' }}>{latestEval?.pt_fall_risk || 'High'}</span>
                                </div>
                            </div>
                        </div>

                        <div style={{ paddingTop: 16, borderTop: '1px solid var(--border-color)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <strong style={{ fontSize: 13 }}>Progress Toward Goals</strong>
                                <span style={{ fontSize: 13, fontWeight: 600 }}>{latestPlan?.progress_pct || '40'}%</span>
                            </div>
                            <div style={{ width: '100%', height: 6, backgroundColor: 'var(--border-color)', borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ width: `${latestPlan?.progress_pct || 40}%`, height: '100%', backgroundColor: 'var(--primary-color)' }}></div>
                            </div>
                            <p style={{ marginTop: 12, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, fontStyle: 'italic' }}>
                                {latestPlan?.progress_note || 'Patient is making gradual progress toward established goals. Continues to benefit from skilled PT services to address strength, balance, and functional mobility.'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ marginTop: 24, padding: 16, border: '1px solid var(--border-color)', borderRadius: 12, backgroundColor: 'var(--bg-main)', display: 'flex', alignItems: 'center', gap: 12 }}>
                <CreditCard size={20} color="var(--primary-color)" />
                <div>
                    <strong style={{ fontSize: 14 }}>Billing Responsibility</strong>
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                        Insurance provider: {patient.insurance_provider || 'Medicare'} · Member ID: {patient.insurance_member_id || '—'} · {latestPlan?.billing_party || 'Primary Insurance'} is billed for these sessions.
                    </p>
                </div>
            </div>

            <div style={{ marginTop: 32 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CheckCircle size={18} color="var(--primary-color)" />
                        <strong style={{ fontSize: 15 }}>Treatment Sessions</strong>
                    </div>
                    <button className="text-button" onClick={refresh}>View All</button>
                </div>
                <div className="table-scroll">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', padding: '12px 8px' }}>Date</th>
                                <th style={{ textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', padding: '12px 8px' }}>Session #</th>
                                <th style={{ textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', padding: '12px 8px' }}>Treatment Focus</th>
                                <th style={{ textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', padding: '12px 8px' }}>Patient Response</th>
                                <th style={{ textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', padding: '12px 8px' }}>Pain Level</th>
                                <th style={{ textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', padding: '12px 8px' }}>Progress</th>
                                <th style={{ textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', padding: '12px 8px' }}>Therapist</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(latestPlan?.treatment_sessions || [
                                { date: '04/10/2024', number: 10, focus: 'Gait training, balance', response: 'Tolerated well. Improved step length with walker.', pain_level: '3/10', progress_pct: 60, therapist: 'K. Patel', credentials: 'PT' },
                                { date: '04/08/2024', number: 9, focus: 'Strengthening (LE), transfers', response: 'Good effort. Required minimal cues.', pain_level: '4/10', progress_pct: 50, therapist: 'K. Patel', credentials: 'PT' },
                                { date: '04/05/2024', number: 8, focus: 'Balance, endurance', response: 'Tolerated well. Required standing tolerance.', pain_level: '4/10', progress_pct: 45, therapist: 'J. Smith', credentials: 'PT' },
                                { date: '04/03/2024', number: 7, focus: 'Gait training', response: 'Decreased assistance needed. Positive response.', pain_level: '5/10', progress_pct: 40, therapist: 'J. Smith', credentials: 'PT' },
                                { date: '04/01/2024', number: 6, focus: 'Strengthening (LE), ROM', response: 'Fatigued quickly but completed activities.', pain_level: '5/10', progress_pct: 35, therapist: 'K. Patel', credentials: 'PT' },
                            ]).map((s, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                    <td style={{ padding: '12px 8px', fontSize: 13 }}>{s.date || '—'}</td>
                                    <td style={{ padding: '12px 8px', fontSize: 13 }}>{s.number || i + 1}</td>
                                    <td style={{ padding: '12px 8px', fontSize: 13, fontWeight: 500 }}>{s.focus || '—'}</td>
                                    <td style={{ padding: '12px 8px', fontSize: 13 }}>{s.response || '—'}</td>
                                    <td style={{ padding: '12px 8px', fontSize: 13 }}>{s.pain_level || '—'}</td>
                                    <td style={{ padding: '12px 8px', fontSize: 13 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <div style={{ width: 60, height: 6, backgroundColor: 'var(--border-color)', borderRadius: 3, overflow: 'hidden' }}>
                                                <div style={{ width: `${s.progress_pct || 0}%`, height: '100%', backgroundColor: 'var(--primary-color)' }}></div>
                                            </div>
                                            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{s.progress_pct || 0}%</span>
                                        </div>
                                    </td>
                                    <td style={{ padding: '12px 8px', fontSize: 13 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <span>{s.therapist || '—'}</span>
                                            <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontStyle: 'italic' }}>{s.credentials || 'PT'}</span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
}
